import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useScrollAnimation, useScrollAnimationMultiple } from '@/hooks/useScrollAnimation';
import HeroCarousel from './components/HeroCarousel';

export default function Home() {
  const { t } = useTranslation();
  const heroRef = useRef<HTMLDivElement>(null);

  const aboutAnim = useScrollAnimation();
  const servicesAnim = useScrollAnimation();
  const whyAnim = useScrollAnimation();
  const clientsAnim = useScrollAnimation();
  const ctaAnim = useScrollAnimation();

  const serviceCount = 11;
  const { setRef: setServiceRef, visible: servicesVisible } = useScrollAnimationMultiple(serviceCount, 0.1);

  const engineeringServices = [
    { key: 'mechanical', icon: 'ri-settings-3-line' },
    { key: 'electricity', icon: 'ri-flashlight-line' },
    { key: 'hydraulic', icon: 'ri-water-flash-line' },
    { key: 'electromechanics', icon: 'ri-building-2-line' },
    { key: 'hvac', icon: 'ri-windy-line' },
    { key: 'electronics', icon: 'ri-cpu-line' },
  ];

  const additionalServices = [
    { key: 'tickets', icon: 'ri-plane-line' },
    { key: 'tourism', icon: 'ri-map-pin-2-line' },
    { key: 'office', icon: 'ri-stack-line' },
    { key: 'visas', icon: 'ri-passport-line' },
    { key: 'rentacar', icon: 'ri-car-line' },
  ];

  const clients = [
    'United Nations - FAO',
    'TV Zimbo',
    'Shopping Big-One',
    'SOSEAL',
    'Grupo Simples',
    'Primeiro Andar',
    'Sófrango',
    'Instituto Nacional de Estatística',
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Carousel */}
      <div ref={heroRef}>
        <HeroCarousel />
      </div>

      {/* About Section */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div
            ref={aboutAnim.ref}
            className={`grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center transition-all duration-700 ${
              aboutAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div className="order-2 lg:order-1">
              <span className="text-accent-500 font-medium text-sm tracking-wider uppercase mb-3 block">
                {t('home.about.title')}
              </span>
              <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-950 mb-6">
                {t('home.about.title')}
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-4 text-base">
                {t('home.about.text1')}
              </p>
              <p className="text-foreground-700 leading-relaxed mb-6 text-base">
                {t('home.about.text2')}
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 font-medium text-sm transition-colors"
              >
                {t('home.about.cta')}
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                <img
                  src="https://public.readdy.ai/ai/img_res/edited_eeaf4648612a35e1627bf86152f76d56_f6c84e04.jpg"
                  alt="DEIASPART Workshop"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                  <p className="text-background-50 font-heading font-semibold text-lg">{t('home.about.since')}</p>
                  <p className="text-background-200 text-sm">{t('home.about.location')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div
            ref={servicesAnim.ref}
            className={`text-center mb-12 md:mb-16 transition-all duration-700 ${
              servicesAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <span className="text-accent-500 font-medium text-sm tracking-wider uppercase mb-3 block">
              {t('home.services.subtitle')}
            </span>
            <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-950 mb-4">
              {t('home.services.title')}
            </h2>
          </div>

          {/* Engineering Services */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-10">
            {engineeringServices.map((service, i) => (
              <div
                key={service.key}
                ref={setServiceRef(i)}
                className={`bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/70 hover:border-primary-300 transition-all duration-300 hover:-translate-y-1 ${
                  servicesVisible[i] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                style={{ transitionDuration: '600ms', transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-12 h-12 rounded-lg bg-primary-50 flex items-center justify-center mb-4">
                  <i className={`${service.icon} text-primary-500 text-xl w-6 h-6 flex items-center justify-center`}></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 mb-2 text-base">
                  {t(`service.${service.key}.name`)}
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  {t(`service.${service.key}.desc`)}
                </p>
              </div>
            ))}
          </div>

          {/* Additional Services */}
          <div className="bg-accent-50/50 rounded-2xl p-6 md:p-8 border border-accent-200/40 mb-10">
            <h3 className="font-heading font-semibold text-lg text-foreground-950 mb-5 text-center">
              {t('home.services.additionalTitle')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {additionalServices.map((service, i) => (
                <div
                  key={service.key}
                  ref={setServiceRef(i + 6)}
                  className={`bg-background-50 rounded-lg p-4 border border-background-200/70 hover:border-accent-300 transition-all duration-300 text-center ${
                    servicesVisible[i + 6] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                  style={{ transitionDuration: '600ms', transitionDelay: `${(i + 6) * 80}ms` }}
                >
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mx-auto mb-3">
                    <i className={`${service.icon} text-accent-600 text-lg w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <h4 className="font-medium text-foreground-950 text-sm mb-1">
                    {t(`service.${service.key}.name`)}
                  </h4>
                  <p className="text-foreground-600 text-xs leading-relaxed">
                    {t(`service.${service.key}.desc`)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-7 py-3.5 rounded-lg font-medium text-sm transition-colors whitespace-nowrap"
            >
              {t('home.services.viewAll')}
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div
            ref={whyAnim.ref}
            className={`text-center mb-12 md:mb-16 transition-all duration-700 ${
              whyAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-950 mb-4">
              {t('home.why.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: 'ri-speed-line',
                title: t('home.why.response'),
                desc: t('home.why.responseDesc'),
                color: 'primary',
              },
              {
                icon: 'ri-customer-service-2-line',
                title: t('home.why.assistance'),
                desc: t('home.why.assistanceDesc'),
                color: 'accent',
              },
              {
                icon: 'ri-award-line',
                title: t('home.why.experience'),
                desc: t('home.why.experienceDesc'),
                color: 'primary',
              },
            ].map((item, i) => (
              <div
                key={i}
                className={`text-center p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/70 transition-all duration-700 ${
                  whyAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <div className={`w-14 h-14 md:w-16 md:h-16 rounded-xl ${
                  item.color === 'primary' ? 'bg-primary-50' : 'bg-accent-50'
                } flex items-center justify-center mx-auto mb-5`}>
                  <i className={`${item.icon} text-xl md:text-2xl w-7 h-7 md:w-8 md:h-8 flex items-center justify-center ${
                    item.color === 'primary' ? 'text-primary-500' : 'text-accent-500'
                  }`}></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 text-base md:text-lg mb-3">
                  {item.title}
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section className="py-16 md:py-24 bg-primary-950">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div
            ref={clientsAnim.ref}
            className={`text-center mb-10 md:mb-14 transition-all duration-700 ${
              clientsAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-background-50 mb-3">
              {t('home.clients.title')}
            </h2>
            <p className="text-background-300 text-base max-w-xl mx-auto">
              {t('home.clients.subtitle')}
            </p>
          </div>

          <div
            className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 transition-all duration-700 ${
              clientsAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            {clients.map((client, i) => (
              <div
                key={i}
                className="bg-primary-900/60 border border-primary-800/50 rounded-xl p-4 md:p-5 text-center hover:bg-primary-900/80 transition-colors"
              >
                <p className="text-background-200 font-medium text-sm">{client}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-accent-500">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div
            ref={ctaAnim.ref}
            className={`text-center max-w-2xl mx-auto transition-all duration-700 ${
              ctaAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-background-50 mb-4">
              {t('home.cta.title')}
            </h2>
            <p className="text-background-100 text-base md:text-lg mb-8 leading-relaxed">
              {t('home.cta.subtitle')}
            </p>
            <a
              href={`mailto:geral@deiaspart.co.ao?subject=${encodeURIComponent(t('home.cta.mailSubject'))}`}
              className="inline-flex items-center gap-2 bg-background-50 hover:bg-background-100 text-foreground-950 px-8 py-4 rounded-lg font-medium text-sm transition-colors whitespace-nowrap"
            >
              {t('home.cta.button')}
              <i className="ri-mail-send-line w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}